import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { path, pathOr, propOr } from 'ramda';
import { push, go } from 'react-router-redux';
import FsCard from '../../../../src/components/lib/FsSection';
import FSFlatButton from '../../../../src/components/lib/FSFlatButton';
import FlightHeader from './children/FlightHeader/FlightHeader';
import FlightPositionalInformationSection from './children/FlightPositionalInformationSection/FlightPositionalInformationSection';
import FlightInformation from './children/FlightInformation/FlightInformation';
import FlightSearch from '../../../FlightTracker/components/Search/children/FlightSearch/FlightSearch';
import BannerAd from '../../../../shared/components/BannerAd/BannerAd';
import { FlightTracker as flightTrackerAds } from '../../../FlightTracker/components/ads';
import componentBase from '../../../../src/lib/component-base';
import { updateSegments } from '../../redux/actions';

const extractPathParams = propOr({}, 'params');

@componentBase('FlightTracker')
export default class FlightTracker extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func,
    error: PropTypes.object,
    flight: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object,
    params: PropTypes.object,
    route: PropTypes.object,
    segments: PropTypes.array,
    style: PropTypes.object,
  };

  constructor(props, context) {
    super(props, context);

    this.muiDefaultProps = {
      buttonStyle: FSFlatButton.style({
        minWidth: '100%',
        margin: '5px auto',
        minHeight: '40px',
        lineHeight: '12pt',
        fontSize: '12pt',
      }),
      buttonLabelStyle: {
        width: '90%',
        textAlign: 'center',
        textTransform: 'capitalize',
        paddingLeft: '0',
        paddingRight: '0',
        wordWrap: 'normal',
      },
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.error) {
      this.logger('Refresh error:', nextProps.error);
    }
  }

  componentWillUnmount() {
    this.props.dispatch(updateSegments([])); // reset back to no information
  }

  openFlightAlertManager = (source) => {
    if (source === 'callout') {
      this.context.reportButtonPress('CalloutFlightAlerts');
    } else {
      this.context.reportButtonPress('FlightAlerts');
    }

    const { segments, match } = this.props;
    const {
      carrier, flightNumber, year, month, day,
    } = extractPathParams(match);
    const flight = segments[this.selectedRouteIndex];
    const url = [
      '/flight-alerts/new',
      carrier,
      flightNumber,
      year,
      month,
      day,
      flight.ticketHeader.departureAirport.fs,
      flight.ticketHeader.arrivalAirport.fs,
      'true',
    ].join('/');
    this.props.dispatch(push(url));
  }

  openFlightMap = () => {
    const { segments, match } = this.props;
    const {
      carrier, flightNumber, year, month, day,
    } = extractPathParams(match);
    const flight = segments[this.selectedRouteIndex];

    this.props.dispatch(push([
      '/flight-tracker',
      carrier,
      flightNumber,
      year,
      month,
      day,
      flight.flightId,
      'map',
    ].join('/')));
    this.context.reportButtonPress('OpenFlightMap');
  }

  openDetails = () => {
    this.props.dispatch(go(-1));
    this.context.reportButtonPress('BackToFlightTracker');
  }

  metaTags = (flight) => {
    const flightString = `(${flight.ticketHeader.carrier.fs}) ${flight.ticketHeader.carrier.name} #${flight.ticketHeader.flightNumber}`;
    const title = `${flightString} Flight Tracker`;
    const description = `Track ${flightString} live on the FlightStats Flight Tracker`;
    const keywords = `${flight.ticketHeader.carrier.fs} ${flight.ticketHeader.flightNumber} flight tracker, ${flight.ticketHeader.carrier.fs} ${flight.ticketHeader.flightNumber} flight status, ${flight.ticketHeader.carrier.name} ${flight.ticketHeader.flightNumber} flight tracker, ${flight.ticketHeader.carrier.name} ${flight.ticketHeader.flightNumber} flight status`;
    return {
      title,
      meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: keywords },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
      ],
    };
  };

  flightMapButton = () => (
    <div style={{ margin: '20px 0' }}>
      <div style={{ margin: '20px 10px 0' }}>
        <FSFlatButton
          id='flightMapButton'
          label='TRACK FLIGHT ON MAP'
          backgroundColor='rgb(255, 168, 39)'
          onClick={this.openFlightMap}
          style={this.muiDefaultProps.buttonStyle}
          labelStyle={this.muiDefaultProps.buttonLabelStyle}
        />
      </div>
    </div>
  );

  render() {
    const {
      flight, match, dispatch, segments, location,
    } = this.props;
    const { pathname } = location;
    const { params } = match;

    let results = '';
    let flightSearch = <FlightSearch />;
    if (segments) {
      let selected = false;
      this.selectedRouteIndex = 0;
      if (params) {
        segments.forEach((segment, i) => {
          if (`${segment.flightId}` === `${params.flightId}`) {
            this.selectedRouteIndex = i;
            selected = true;
          } else if (!params.flightId && !segment.isLanded &&
                    (segment.isTracking || segment.isScheduled) && !selected) {
            this.selectedRouteIndex = i;
            selected = true;
          }
        });
      }

      let selectedFlight = segments[this.selectedRouteIndex];
      if (flight && selectedFlight && flight.flightId === selectedFlight.flightId) {
        // selectedFlight from segments array may be stale, just replace with polling results
        selectedFlight = flight;
      }

      if (selectedFlight) {
        let map = '';
        let mapButton = null;
        const { flightId } = selectedFlight;
        const fullParams = params.flightId ? params : { ...params, flightId };
        const meta = this.metaTags(selectedFlight);
        const mobileMap = pathname && pathname.includes('/map');
        const mediaValue = pathOr({}, ['mediaBreakpoint'], this.context);
        const isMobile = path(['isMobile'], mediaValue) || path(['value'], mediaValue) <= 768;
        const subscriptionActive = this.context &&
                                   this.context.currentUser &&
                                   this.context.currentUser.subscriptionActive;

        // If there is a flightId (required for flick)
        // and it is a tablet res or higher OR not .isMobile
        // or it is a mobile device on the /map route
        if (flightId && (!isMobile || mobileMap)) {
          // Don't show 300x250 ad on mobile screen
          // otherwise show them on desktop

          map = (
            <FsCard>
              <FlightPositionalInformationSection
                key={flightId}
                flight={selectedFlight}
                showAds={!isMobile}
                params={fullParams}
              />
            </FsCard>
          );
        } else {
          // This should only trigger on a mobile device
          mapButton = this.flightMapButton();
        }

        // If it is a mobile device on the /map route, show the header with the map
        // Else show the flight ticket page with/without the map
        if (mobileMap) {
          flightSearch = null;
          results = (
            <section>
              <Helmet {...meta} />
              <BannerAd
                topAd
                mobileAd={flightTrackerAds.topMobile}
                subscriptionActive={subscriptionActive}
              />
              <FlightHeader {...selectedFlight.resultHeader} />
              <div style={{ margin: '20px 10px 0' }}>
                <FSFlatButton
                  id='backToDetailsButton'
                  label='BACK TO FLIGHT TRACKER'
                  backgroundColor='rgb(255, 168, 39)'
                  onClick={this.openDetails}
                  style={this.muiDefaultProps.buttonStyle}
                  labelStyle={this.muiDefaultProps.buttonLabelStyle}
                />
              </div>
              {map}
            </section>
          );
        } else {
          results = (
            <section>
              <Helmet {...meta} />
              <BannerAd
                topAd
                mobileAd={flightTrackerAds.bannerMobile}
                desktopAd={flightTrackerAds.banner}
                subscriptionActive={subscriptionActive}
              />
              <FlightHeader {...selectedFlight.resultHeader} />
              {map}
              {mapButton}
              <FsCard>
                <FlightInformation
                  {...selectedFlight}
                  segments={this.props.segments}
                  selectedRouteIndex={this.selectedRouteIndex}
                  openFlightAlertManager={this.openFlightAlertManager}
                  isMobile={isMobile}
                  params={fullParams}
                  dispatch={dispatch}
                  subscriptionActive={subscriptionActive}
                />
              </FsCard>
            </section>
          );
        }
      }
    }

    return (
      <div style={this.props.style}>
        {flightSearch}
        {results}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/SingleFlight/children/FlightTracker/FlightTracker.js